import React, { useState, useEffect, useRef } from 'react';
import { FormControlLabel, Switch, Button, TextField, useTheme } from '@mui/material';
import { 
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useUnsubscribeSMS,
} from '../../hooks/useNotifications';
import { useResendSMSVerification } from '../../hooks/useResendVerification';

interface SMSNotificationCardProps {
  onMessage?: (message: string) => void;
}

export const SMSNotificationCard: React.FC<SMSNotificationCardProps> = ({
  onMessage,
}) => {
  const theme = useTheme();
  const { data: preferences } = useNotificationPreferences();
  const updatePreferencesMutation = useUpdateNotificationPreferences();
  const unsubscribeSMSMutation = useUnsubscribeSMS();
  const resendSMSMutation = useResendSMSVerification();

  // Local state for form management
  const [textChecked, setTextChecked] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [editPhone, setEditPhone] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [isOnStopList, setIsOnStopList] = useState(false);
  
  // Ref to preserve switch state during resend verification operations
  const preserveTextCheckedRef = useRef<boolean | null>(null);

  // Sync local state with API preferences
  useEffect(() => {
    if (preferences) {
      // Only sync switch state if we're not preserving it (during resend verification)
      if (preserveTextCheckedRef.current === null) {
        setTextChecked(preferences.smsSubscribed);
      } else {
        setTextChecked(preserveTextCheckedRef.current);
      }
      setPhone(preferences.phoneNumber || '');
      // Clear STOP list status if SMS is confirmed (user has resubscribed)
      if (preferences.smsConfirmed) {
        setIsOnStopList(false);
      }
    }
  }, [preferences]);

  // Show verification status messages
  const getSMSStatusMessage = () => {
    if (!preferences?.phoneNumber) return '';
    if (preferences.smsConfirmed) return 'Phone verified ✓';
    if (preferences.smsVerificationToken) return 'Verification SMS sent - please reply with CONFIRM';
    return 'Phone not verified';
  };

  const validatePhone = (value: string) => {
    if (!value) return 'Phone number is required';
    // US/Canada 10-digit phone number
    const re = /^\d{10}$/;
    return re.test(value.replace(/\D/g, '')) ? '' : 'Invalid phone number (10 digits)';
  };

  const handleResendSMS = async () => {
    try {
      // Preserve current switch state before mutation
      preserveTextCheckedRef.current = textChecked;
      const result = await resendSMSMutation.mutateAsync();
      if (result.success) {
        onMessage?.('Text sent!');
        setIsOnStopList(false); // Clear STOP list status on successful send
      } else {
        // Check response.data for error message
        const errorMsg = result.error || result.message || 'Text failed to send';
        onMessage?.(errorMsg);
        // If it's a STOP list error, track it in component state
        if (errorMsg.includes('STOP list')) {
          setIsOnStopList(true);
        } else {
          setIsOnStopList(false);
        }
      }
      // Keep ref set for a bit longer to handle async refetch
      setTimeout(() => {
        preserveTextCheckedRef.current = null;
      }, 1000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error.message || 'Text failed to send';
      onMessage?.(errorMessage);
      // If it's a STOP list error, track it in component state
      if (errorMessage.includes('STOP list')) {
        setIsOnStopList(true);
      } else {
        setIsOnStopList(false);
      }
      preserveTextCheckedRef.current = null; // Clear on error
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTextChecked(checked);
    
    if (!checked) {
      setPhoneError('');
      setEditPhone(false);
      // Unsubscribe from SMS notifications
      unsubscribeSMSMutation.mutate(undefined, {
        onSuccess: () => {
          onMessage?.('Successfully unsubscribed from SMS notifications');
        },
        onError: (error: any) => {
          onMessage?.(`Failed to unsubscribe: ${error.message}`);
        }
      });
    }
  };

  const handlePhoneInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingPhone(event.target.value);
    setPhoneError(validatePhone(event.target.value));
  };

  const handleSave = () => {
    if (!editPhone) return;
    
    const err = validatePhone(pendingPhone);
    setPhoneError(err);
    if (err) return;
    
    setPhone(pendingPhone);
    setEditPhone(false);
    
    // Update preferences in the database
    updatePreferencesMutation.mutate(
      {
        phoneNumber: pendingPhone,
        smsSubscribed: true,
      },
      {
        onSuccess: () => {
          onMessage?.('Notification preferences updated successfully!');
        },
        onError: (error: any) => {
          onMessage?.(`Failed to update preferences: ${error.message}`);
        }
      }
    );
  };

  const isSaveDisabled = editPhone && (!!phoneError || !pendingPhone);

  return (
    <>
      <FormControlLabel
        control={<Switch checked={textChecked} onChange={handleTextChange} />}
        label="Text Message Notifications"
      />
      <div style={{ marginLeft: 32 }}>
        {textChecked ? (
          editPhone ? (
            <>
              <TextField
                label="Phone Number"
                value={pendingPhone}
                onChange={handlePhoneInputChange}
                error={!!phoneError}
                helperText={phoneError}
                fullWidth
                margin="normal"
                type="tel"
                autoComplete="tel"
                placeholder="1234567890"
                sx={{ maxWidth: 320 }}
              />
              <div style={{ color: theme.palette.text.disabled, fontSize: 13, marginTop: 4 }}>
                By providing your phone number, you agree to receive text messages from ODP.Golf. Message and data rates may apply. Message frequency varies.<br/>
                You can opt out anytime by replying STOP, or get help by replying HELP. View our <a href="/privacy">privacy policy</a> for more information.
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginRight: 8, marginTop: 8 }}
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                style={{ marginTop: 8 }}
                onClick={() => { setEditPhone(false); setPhoneError(''); }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <span style={{ color: theme.palette.text.primary, fontSize: 15 }}>
                {phone ? `Current phone: ${phone}` : 'No phone number set.'}
              </span>
              <Button
                variant="text"
                size="small"
                style={{ marginLeft: 12 }}
                onClick={() => { setEditPhone(true); setPendingPhone(phone); }}
              >
                {phone ? 'Change' : 'Add'}
              </Button>
              <div style={{ color: theme.palette.text.disabled, fontSize: 13, marginTop: 4 }}>
                By providing your phone number, you agree to receive text messages from ODP.Golf. Message and data rates may apply. Message frequency varies.<br/>
                You can opt out anytime by replying STOP, or get help by replying HELP. View our <a href="/privacy">privacy policy</a> for more information.
              </div>
              {getSMSStatusMessage() && (
                <div style={{ 
                  color: preferences?.smsConfirmed ? theme.palette.success.main : theme.palette.warning.main, 
                  fontSize: 13, 
                  marginTop: 8,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{getSMSStatusMessage()}</span>
                  {preferences?.phoneNumber && !preferences?.smsConfirmed && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResendSMS}
                      disabled={resendSMSMutation.isPending}
                      style={{ fontSize: '11px', padding: '2px 8px', minWidth: 'auto' }}
                    >
                      {resendSMSMutation.isPending ? 'Sending...' : 'Send verification text'}
                    </Button>
                  )}
                </div>
              )}
              {!preferences?.smsConfirmed && preferences?.phoneNumber && isOnStopList && (
                <div style={{ color: theme.palette.error.main, fontSize: 12, marginTop: 6 }}>
                  ⚠️ This phone number is on the STOP list. Please text <b>START</b> or <b>UNSTOP</b> to our Twilio number (+16038369936) to resubscribe.
                </div>
              )}
            </>
          )
        ) : (
          <>
            <span style={{ color: theme.palette.text.primary, fontSize: 15 }}>
              {phone ? `Current phone: ${phone}` : 'No phone number set.'}
            </span>
            {phone && (
              <div style={{ color: theme.palette.text.secondary, fontSize: 12, marginTop: 6 }}>
                Text notifications are turned off. Your phone number remains saved; turn this back on to resume texts.
              </div>
            )}
            {!phone && (
              <div style={{ color: theme.palette.text.disabled, fontSize: 13, marginTop: 4 }}>
                By providing your phone number, you agree to receive text messages from ODP.Golf. Message and data rates may apply. Message frequency varies.<br/>
                You can opt out anytime by replying STOP, or get help by replying HELP. View our <a href="/privacy">privacy policy</a> for more information.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
